/**
 * iter 43 Phase 2 — Proof-of-service recipient chip picker.
 *
 * Renders the customer's `proof_recipients_json` array as removable chips and
 * lets the user add new entries of four kinds:
 *   - customer  (uses customer.email)
 *   - attorney  (uses cases.attorney_email at send-time)
 *   - assigner  (the Core admin who created the job)
 *   - custom    (a free-form email address typed inline)
 *
 * Stored shape (matches the resolver in routers/proofs_router.send_proof):
 *   [{ "kind": "customer" | "attorney" | "assigner" | "custom",
 *      "value": "you@firm.com" | null }]
 */
import { useState } from "react";
import { X, Plus } from "@phosphor-icons/react";

const KIND_LABEL = {
  customer: "CUSTOMER",
  attorney: "ATTORNEY",
  assigner: "ASSIGNING ADMIN",
  custom: "CUSTOM",
};
const KIND_COLOR = {
  customer: "#0ea5e9",
  attorney: "#a855f7",
  assigner: "#16a34a",
  custom: "#737373",
};

export default function ProofRecipientsPicker({ value, onChange, testid = "proof-recipients" }) {
  const list = Array.isArray(value) ? value : [];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ kind: "customer", value: "" });

  const add = () => {
    if (draft.kind === "custom" && !draft.value.trim()) return;
    const entry = draft.kind === "custom"
      ? { kind: "custom", value: draft.value.trim() }
      : { kind: draft.kind, value: null };
    onChange([...list, entry]);
    setDraft({ kind: "customer", value: "" });
    setAdding(false);
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-2">
        {list.length === 0 && (
          <span className="text-xs text-muted-foreground" data-testid={`${testid}-empty`}>
            No recipients yet — proofs will not be auto-emailed for this customer.
          </span>
        )}
        {list.map((r, idx) => (
          <span
            key={`${r.kind}-${r.value || idx}`}
            className="badge text-xs"
            style={{ background: KIND_COLOR[r.kind] || "#999", color: "#fff" }}
            data-testid={`${testid}-chip-${idx}`}
          >
            {KIND_LABEL[r.kind] || r.kind}
            {r.value ? `: ${r.value}` : ""}
            <button
              type="button"
              className="ml-1 hover:opacity-80"
              onClick={() => remove(idx)}
              aria-label="Remove"
              data-testid={`${testid}-remove-${idx}`}
            ><X size={12} /></button>
          </span>
        ))}
      </div>

      {adding ? (
        <div className="flex gap-2 items-center">
          <select
            className="input"
            value={draft.kind}
            onChange={e => setDraft(d => ({ ...d, kind: e.target.value }))}
            data-testid={`${testid}-kind-select`}
          >
            <option value="customer">Customer email</option>
            <option value="attorney">Attorney of record</option>
            <option value="assigner">Assigning Core admin</option>
            <option value="custom">Custom email…</option>
          </select>
          {draft.kind === "custom" && (
            <input
              className="input flex-1"
              placeholder="someone@example.com"
              type="email"
              value={draft.value}
              onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
              data-testid={`${testid}-custom-input`}
            />
          )}
          <button type="button" className="btn btn-primary btn-sm" onClick={add} data-testid={`${testid}-confirm`}>ADD</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>CANCEL</button>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setAdding(true)}
          data-testid={`${testid}-add-btn`}
        ><Plus size={12} /> ADD RECIPIENT</button>
      )}
    </div>
  );
}
